import { useEffect, useRef } from "react";
import { useAuth } from "@/lib/auth";
import axios from "axios";
import { getApiUrl } from "@/lib/apiUrl";

const API_BASE_URL = `${getApiUrl()}/api/v1`;

interface TokenRotationOptions {
  /** Whether proactive rotation is enabled */
  enabled?: boolean;
  /** How often to rotate the refresh token (ms). Default: 1 hour */
  rotationIntervalMs?: number;
}

/**
 * Proactively rotates the refresh token on a schedule to reduce
 * the window for stolen token attacks. Old tokens are invalidated
 * server-side on each rotation.
 *
 * This works for ALL auth providers (Google, Apple, email, etc.)
 * and is independent of the Google-specific silent re-auth.
 */
export function useTokenRotation(options: TokenRotationOptions = {}) {
  const {
    enabled = true,
    rotationIntervalMs = 60 * 60 * 1000, // 1 hour
  } = options;

  const { isAuthenticated } = useAuth();
  const rotationInProgress = useRef(false);
  const lastRotation = useRef<number>(Date.now());

  useEffect(() => {
    if (!enabled || !isAuthenticated) {
      return;
    }

    const rotateToken = async () => {
      if (rotationInProgress.current) return;

      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) return;

      rotationInProgress.current = true;

      // Exponential backoff: 3 attempts with 5s, 15s, 45s delays
      // Skip retries on 401/403 — token is revoked, not a transient failure
      const maxRetries = 3;
      const baseDelayMs = 5_000;

      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
            refreshToken,
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data.data;
          localStorage.setItem("access_token", accessToken);
          localStorage.setItem("refresh_token", newRefreshToken);
          lastRotation.current = Date.now();
          break; // success
        } catch (err: unknown) {
          const status = (err as { response?: { status?: number } })?.response?.status;
          // Auth failures are permanent — don't retry, let 401 interceptor handle it
          if (status === 401 || status === 403) break;
          // Transient error — wait before retrying
          if (attempt < maxRetries - 1) {
            await new Promise((r) => setTimeout(r, baseDelayMs * Math.pow(3, attempt)));
          }
        }
      }

      rotationInProgress.current = false;
    };

    const interval = setInterval(() => {
      const timeSinceLastRotation = Date.now() - lastRotation.current;
      if (timeSinceLastRotation >= rotationIntervalMs) {
        rotateToken();
      }
    }, 60 * 1000); // Check every minute, rotate on schedule

    return () => {
      clearInterval(interval);
    };
  }, [enabled, isAuthenticated, rotationIntervalMs]);
}
